import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Upload, Loader2, X, Camera, Square } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { SEV_META, STATUS_META, TYPE_META, prettyType } from "@/lib/defects";
import PhotoAnnotator from "@/components/PhotoAnnotator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = {
  title: "", defect_type: "crack", severity: "medium", status: "open",
  location_note: "", measurement: "", photos: [], boxes: [], position: null, normal: null, ai_analysis: null,
};

export default function DefectDialog({ open, onOpenChange, projectId, levelId, placement, existing, initialCapture, assetType = "parking", onSaved, onPickMeasurement }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [annotate, setAnnotate] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (existing) { setForm({ ...empty, ...existing }); return; }
    const base = { ...empty, position: placement?.position || null, normal: placement?.normal || null };
    if (initialCapture) {
      base.photos = [initialCapture];
      setForm(base);
      setAnnotate(initialCapture);
      classifyPath(initialCapture);
    } else {
      setForm(base);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing, placement, initialCapture]);

  const classifyPath = async (p) => {
    setAnalyzing(true);
    try {
      const resp = await fetch(fileUrl(p));
      const blob = await resp.blob();
      const b64 = await fileToBase64(blob);
      const { data } = await api.post("/ai/classify-defect", { image_base64: b64, asset_type: assetType });
      setForm((prev) => ({
        ...prev,
        title: prev.title || data.title || "Defect",
        defect_type: data.defect_type || prev.defect_type,
        severity: data.severity || prev.severity,
        ai_analysis: data,
      }));
      toast.success("AI classified the captured defect");
    } catch { /* user can classify manually */ }
    finally { setAnalyzing(false); }
  };

  const fileToBase64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setForm((prev) => ({ ...prev, photos: [...prev.photos, ...paths] }));
      toast.success(`${paths.length} photo(s) uploaded`);
      // auto-run AI on first uploaded photo if title empty
      if (!existing && !form.title) await runAI(files[0]);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const runAI = async (file) => {
    setAnalyzing(true);
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/ai/classify-defect", { image_base64: b64, asset_type: assetType });
      setForm((prev) => ({
        ...prev,
        title: prev.title || data.title || "Defect",
        defect_type: data.defect_type || prev.defect_type,
        severity: data.severity || prev.severity,
        ai_analysis: data,
      }));
      toast.success("AI classified the defect", { description: `${prettyType(data.defect_type)} · ${data.severity}` });
    } catch (e) {
      toast.error("AI analysis unavailable — enter details manually");
    } finally {
      setAnalyzing(false);
    }
  };

  const removePhoto = (p) => setForm((prev) => ({ ...prev, photos: prev.photos.filter((x) => x !== p), boxes: (prev.boxes || []).filter((b) => b.photo !== p) }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Give the defect a title");
    setSaving(true);
    try {
      const payload = { ...form, project_id: projectId, level_id: levelId };
      let saved;
      if (existing) {
        const { data } = await api.put(`/defects/${existing.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/defects", payload);
        saved = data;
      }
      toast.success(existing ? "Defect updated" : "Defect logged");
      onSaved && onSaved(saved);
      onOpenChange(false);
    } catch {
      toast.error("Could not save defect");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-white/10 text-foreground sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#C5A24A]" />
            {existing ? "Edit Defect" : "Log Defect"}
            {form.position && <span className="text-[10px] font-normal text-[#34C759] px-2 py-0.5 rounded-full bg-[#34C759]/10">pinned in 3D</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Photos + AI */}
          <div>
            <Label className="label-caps text-muted-foreground">Defect Photos</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {form.photos.map((p) => {
                const pboxes = (form.boxes || []).filter((b) => b.photo === p);
                return (
                  <div key={p} className="relative w-24 h-24 rounded overflow-hidden border border-white/10 group" data-testid="defect-photo-thumb">
                    <img src={fileUrl(p)} alt="defect" className="w-full h-full object-cover" />
                    {pboxes.map((b) => (
                      <div key={b.id} className="absolute pointer-events-none" style={{ left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.w * 100}%`, height: `${b.h * 100}%`, border: `3px solid ${SEV_META[b.severity]?.color || "#FFCC00"}`, boxShadow: "0 0 0 1.5px rgba(0,0,0,0.6)" }} />
                    ))}
                    <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/0 group-hover:bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => setAnnotate(p)} data-testid="annotate-photo-button" title="Draw defect boxes" className="w-7 h-7 rounded-full bg-[#C5A24A] flex items-center justify-center hover:bg-[#A8863A]">
                        <Square className="w-3.5 h-3.5 text-white" />
                      </button>
                      <button type="button" onClick={() => removePhoto(p)} data-testid="remove-photo-button" title="Remove photo" className="w-7 h-7 rounded-full bg-black/70 flex items-center justify-center hover:bg-black">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {pboxes.length > 0 && (
                      <span className="absolute top-0.5 left-0.5 text-[9px] px-1 rounded bg-black/75 text-white" data-testid="photo-box-count">{pboxes.length} box{pboxes.length > 1 ? "es" : ""}</span>
                    )}
                  </div>
                );
              })}
              <label className="w-24 h-24 rounded border border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-[#C5A24A] transition-colors" data-testid="upload-photo-label">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-muted-foreground" />}
                <span className="text-[10px] text-muted-foreground mt-1">Upload</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="upload-photo-input" />
              </label>
            </div>
            {form.photos.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={analyzing}
                data-testid="ai-classify-button"
                onClick={async () => {
                  const p = form.photos[0];
                  try {
                    setAnalyzing(true);
                    const resp = await fetch(fileUrl(p));
                    const blob = await resp.blob();
                    const b64 = await fileToBase64(blob);
                    const { data } = await api.post("/ai/classify-defect", { image_base64: b64, asset_type: assetType });
                    setForm((prev) => ({ ...prev, title: data.title || prev.title, defect_type: data.defect_type, severity: data.severity, ai_analysis: data }));
                    toast.success("AI classified the defect");
                  } catch { toast.error("AI analysis unavailable"); }
                  finally { setAnalyzing(false); }
                }}
                className="mt-2 gap-2 border-[#C5A24A]/40 text-[#D8BE86] hover:bg-[#C5A24A]/10 hover:text-[#D8BE86]"
              >
                {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Classify with AI
              </Button>
            )}
          </div>

          {form.ai_analysis?.description && (
            <div className="text-xs bg-[#C5A24A]/8 border border-[#C5A24A]/25 rounded p-3">
              <p className="flex items-center gap-1.5 text-[#D8BE86] font-medium mb-1"><Sparkles className="w-3 h-3" /> AI assessment</p>
              <p className="text-muted-foreground">{form.ai_analysis.description}</p>
              {form.ai_analysis.recommendation && <p className="text-muted-foreground mt-1"><span className="text-white">Fix:</span> {form.ai_analysis.recommendation}</p>}
            </div>
          )}

          <div>
            <Label className="label-caps text-muted-foreground">Title</Label>
            <Input data-testid="defect-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" placeholder="Vertical crack on pillar B4" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-caps text-muted-foreground">Type</Label>
              <Select value={form.defect_type} onValueChange={(v) => setForm({ ...form, defect_type: v })}>
                <SelectTrigger data-testid="defect-type-select" className="mt-1.5 bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10">
                  {Object.keys(TYPE_META).map((k) => <SelectItem key={k} value={k}>{TYPE_META[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="defect-severity-select" className="mt-1.5 bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10">
                  {Object.keys(SEV_META).map((k) => <SelectItem key={k} value={k}>{SEV_META[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="defect-status-select" className="mt-1.5 bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10">
                  {Object.keys(STATUS_META).map((k) => <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-caps text-muted-foreground">Location note</Label>
              <Input data-testid="defect-location-input" value={form.location_note} onChange={(e) => setForm({ ...form, location_note: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" placeholder="Pillar B4, north face" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Measurement</Label>
              <Input data-testid="defect-measurement-input" value={form.measurement} onChange={(e) => setForm({ ...form, measurement: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" placeholder="e.g. 1.2m x 3mm" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="defect-save-button" onClick={save} disabled={saving} className="bg-[#C5A24A] hover:bg-[#A8863A] text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : existing ? "Save changes" : "Log defect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <PhotoAnnotator
      open={!!annotate}
      onOpenChange={(v) => { if (!v) setAnnotate(null); }}
      photoUrl={annotate ? fileUrl(annotate) : ""}
      boxes={(form.boxes || []).filter((b) => b.photo === annotate)}
      defaultType={form.defect_type}
      defaultSeverity={form.severity}
      assetType={assetType}
      onSave={(newBoxes) => {
        const photo = annotate;
        setForm((prev) => ({
          ...prev,
          boxes: [...(prev.boxes || []).filter((b) => b.photo !== photo), ...newBoxes.map((b) => ({ ...b, photo }))],
        }));
        setAnnotate(null);
        toast.success(`${newBoxes.length} box(es) saved on photo`);
      }}
    />
    </>
  );
}
